import React, { useEffect } from 'react';
import { FeatureFlags } from '@adp-wfn/mdf-core';

import { IMDFButtonProps, MDFButton } from './MDFButton';
import html2pdf from './html2pdf';

declare const window: any;

export interface IMDFPrintButtonProps extends IMDFButtonProps {
  // ID of element whose contents are to be printed.
  containerId: string;

  // Options specified here: https://github.com/eKoopmans/html2pdf.js#options
  pdfOptions?: object;

  // Whether to use the html2canvas library in the worker
  useHtmlToCanvasLibrary?: boolean;

  // Whether to use the browser's built-in print functionality
  useWindowPrint?: boolean;

  // When true, collapses ancestor elements and hides non-ancestor body children
  // before printing, preventing the browser from generating extra blank pages
  // for off-screen or modal content. Defaults to false (legacy behaviour).
  optimizeModalDialogPrinting?: boolean;
}

export const MDFPrintButton = (props: IMDFPrintButtonProps) => {
  const { containerId, pdfOptions, useHtmlToCanvasLibrary, optimizeModalDialogPrinting, children, ...mdfButtonProps } = props;

  if (!containerId) {
    console.warn('MDFPrintButton required property "containerId" was not provided!');
  }

  useEffect(() => {
    if (!window.html2pdf) {
      window.WFNShell?.addScript('/theme/static/editor/html2pdf.bundle.min.js');
    }
  }, []);

  const printWithBrowser = (element: HTMLElement) => {
    const printClass = 'mdf-print-target-' + Date.now();
    element.classList.add(printClass);

    // Arrays shared by both code paths so the single cleanup function can
    // restore every element that was mutated, regardless of which path ran.
    const collapsedAncestors: { el: HTMLElement; prev: string | null }[] = [];
    const injectedShadowStyles: { root: ShadowRoot; el: HTMLStyleElement }[] = [];
    const hiddenElements: { el: Element; prev: string | null }[] = [];
    const hiddenEmployeeIdBars: { el: Element; prev: string | null }[] = [];

    if (optimizeModalDialogPrinting) {
      // Walk every ancestor from the print target up to <body>, collecting them
      // so we can collapse their height to zero. Each ancestor must stay in the
      // DOM (the target is inside it) but must contribute nothing to the print
      // layout – otherwise the browser generates blank printed pages for every
      // viewport-height worth of invisible layout the ancestor occupies.
      let ancestor = element.parentElement;

      while (ancestor && ancestor !== document.body) {
        collapsedAncestors.push({ el: ancestor, prev: ancestor.getAttribute('style') });

        try {
          // Inline styles with !important beat everything in the cascade,
          // including :host rules in the element's own shadow DOM stylesheet.
          // height:0 + overflow:hidden means the ancestor contributes zero
          // height to the print flow while the absolutely-positioned print
          // target can still render outside this zero-height box.
          // position:static ensures the print target's "position:absolute; top:0"
          // resolves all the way to the Initial Containing Block instead of
          // being trapped inside a positioned ancestor.  Doing this in JS means
          // only the true ancestors are flattened – the CSS rule no longer needs
          // to reach inside the print target at all.
          ancestor.style.setProperty('height', '0', 'important');
          ancestor.style.setProperty('min-height', '0', 'important');
          ancestor.style.setProperty('max-height', '0', 'important');
          ancestor.style.setProperty('overflow', 'hidden', 'important');
          ancestor.style.setProperty('position', 'static', 'important');
        }
        catch (e) {
          // ignore if cannot set style
        }

        // For web components such as sdf-focus-pane, the shadow DOM contains
        // position:fixed elements (backdrop, wrapper) whose styles the global
        // @media print CSS cannot reach across the shadow boundary. Injecting
        // a <style> directly into the shadow root collapses those elements and
        // prevents them from generating blank pages. This style is appended
        // last in the shadow root so it wins on source-order over the
        // Stencil-injected component stylesheet. The :host rule here works
        // because shadow :host rules obey the same cascade as author styles
        // when the injected rule comes later in source order.
        const shadowRoot = ancestor.shadowRoot;

        if (shadowRoot) {
          const shadowStyle = document.createElement('style');
          shadowStyle.textContent = `
            .backdrop-container {
              display: none !important;
            }
            dialog::backdrop {
              display: none !important;
            }
            dialog, .wrapper {
              position: static !important;
              height: 0 !important;
              min-height: 0 !important;
              max-height: 0 !important;
              overflow: hidden !important;
            }
            .wrapper > * {
              position: static !important;
              height: 0 !important;
              min-height: 0 !important;
              max-height: 0 !important;
              overflow: hidden !important;
            }
            :host {
              height: 0 !important;
              min-height: 0 !important;
              max-height: 0 !important;
              overflow: hidden !important;
            }
          `;
          shadowRoot.appendChild(shadowStyle);
          injectedShadowStyles.push({ root: shadowRoot, el: shadowStyle });
        }

        ancestor = ancestor.parentElement;
      }

      // Hide all direct body children that are NOT ancestors of the print target.
      // display:none removes them from the print layout entirely (including their
      // shadow DOM), preventing blank pages from the shell, nav, and sibling modals.
      const ancestorElements = new Set(collapsedAncestors.map((a) => a.el));

      document.querySelectorAll('body > *').forEach((el) => {
        if (!ancestorElements.has(el as HTMLElement) && el !== element) {
          hiddenElements.push({ el, prev: el.getAttribute('style') });

          try {
            (el as HTMLElement).style.setProperty('display', 'none', 'important');
          }
          catch (e) {
            // ignore if cannot set style
          }
        }
      });
    }

    // Always hide wfn-employee-id-bar elements outside the print target,
    // regardless of the optimizeModalDialogPrinting flag.
    // Skip any that were already captured by the body > * sweep above to avoid
    // double-processing: if the same element appears in both hiddenElements and
    // hiddenEmployeeIdBars, cleanup would incorrectly re-hide it after printing.
    const alreadyHidden = new Set(hiddenElements.map((h) => h.el));

    document.querySelectorAll('wfn-employee-id-bar').forEach((el) => {
      if (!element.contains(el) && !alreadyHidden.has(el)) {
        hiddenEmployeeIdBars.push({ el, prev: el.getAttribute('style') });

        try {
          (el as HTMLElement).style.setProperty('display', 'none', 'important');
        }
        catch (e) {
          // ignore if cannot set style
        }
      }
    });

    const printStyle = document.createElement('style');
    printStyle.id = 'mdf-print-styles';
    printStyle.textContent = `
      @media print {
        body * {
          visibility: hidden !important;
        }
        ${!optimizeModalDialogPrinting
          ? `body *:not([class*="ag-"]):not([id*="ag-"]):not([class*="mdf-print-target-"]) {
          position: static !important;
        }`
          : ''}
        .${printClass}, .${printClass} * {
          visibility: visible !important;
          print-color-adjust: exact;
        }
        .${printClass} {
          position: absolute !important;
          left: 0 !important;
          top: 0 !important;
          width: 100% !important;
        }

        /* DualMultiSelect: make ms-container the containing block for the
         * absolutely-positioned button overlay so that left:50% resolves to
         * exactly the midpoint of the flex row (where the two panels meet),
         * independent of the vdl-row negative margins on the parent.
         * Vertically center the overlay via CSS to avoid the hardcoded inline
         * top:90.5px magic number being thrown off by print font rendering. */
        .${printClass} .ms-container {
          position: relative !important;
        }
        .${printClass} .vdl-dual-select__button-overlay {
          top: 50% !important;
          transform: translateY(-50%) !important;
        }
      }
    `;

    document.head.appendChild(printStyle);

    const cleanup = () => {
      collapsedAncestors.forEach(({ el, prev }) => {
        if (prev !== null) {
          el.setAttribute('style', prev);
        }
        else {
          el.removeAttribute('style');
        }
      });

      hiddenElements.forEach(({ el, prev }) => {
        if (prev !== null) {
          el.setAttribute('style', prev);
        }
        else {
          el.removeAttribute('style');
        }
      });

      hiddenEmployeeIdBars.forEach(({ el, prev }) => {
        if (prev !== null) {
          el.setAttribute('style', prev);
        }
        else {
          el.removeAttribute('style');
        }
      });

      injectedShadowStyles.forEach(({ root, el }) => {
        try {
          root.removeChild(el);
        }
        catch (e) {
          // ignore
        }
      });

      const styleElement = document.getElementById('mdf-print-styles');

      if (styleElement) {
        styleElement.remove();
      }

      element.classList.remove(printClass);
      window.removeEventListener('afterprint', cleanup);
    };

    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  const onClick = () => {
    if (containerId) {
      const element = document.getElementById(containerId);

      if (element) {
        if (props.useWindowPrint) {
          printWithBrowser(element);
        }
        else if (FeatureFlags.hasFeature('wfnHtml2Pdf')) {
          void html2pdf(element, { ...pdfOptions, useHtmlToCanvasLibrary });
        }
        else if (window.html2pdf) {
          window.html2pdf(element, pdfOptions);
        }
      }
    }
  };

  return <MDFButton {...mdfButtonProps} onClick={onClick}>{children}</MDFButton>;
};
